import React from 'react'
import { Remark } from 'react-remark'

import { formatCurrency, formatDate, formatDateToDDMMYYYY, makeCreditDescription } from '../utils/utils'
import { formatDocumentTitle } from '../utils/documentTitleUtils'
import { calculatePrintLayout } from '../utils/printLayoutUtils'
import DocToHeader from '../interfaces/DocInactive'
import { DocStateSpecial } from '../enums/DocStates'
import { Payment, PaymentTypeEnum } from '../interfaces/Reservation'

import DocNotes from './DocNotesV2'
import { DocHeader, DocHeaderType } from './DocHeaderV2'
import { OrganizationFrame, OrganizationFrameType } from './OrganizationFrameV2'
import { ClientFrame, ClientFrameType } from './ClientFrameV2'
import { VehicleFrame, VehicleFrameType } from './VehicleFrameV2'
import { SalenoteDet } from './SalenotePreview'
import { DisplayValueEx, DisplayValueWithError } from './DisplayValue'
import { DocStatusHeader } from './DocStatusHeader'
import { GridCost, GridPayment, GridVeh } from './ReservationNvvDetailV2'

const SalenoteDetail: React.FC<{
  salenote: SalenoteDet
  errors: { [key: string]: string }
  esAprobada?: boolean
  esAnulada?: boolean
  showPurchaseLinks?: boolean // NEW: Control purchase link visibility
}> = ({ salenote, errors, esAprobada, esAnulada, showPurchaseLinks = false }) => {
  const myNumberNvv = salenote?.number === 0 ? 'XX' : salenote?.number
  //  const isAnulada = !!salenote?.voidedAt

  let tituloAnulada = ''
  let fechaFinText = ''
  let fechaFinValue = ''

  if (esAnulada) {
    tituloAnulada = 'NULA'
  } else if (!esAprobada) {
    tituloAnulada = 'PENDIENTE'
  }

  if (esAprobada) {
    fechaFinText = `Fecha de aprobación`
    fechaFinValue = formatDateToDDMMYYYY(new Date(salenote?.approvedAt ?? new Date()))
  }

  // Format title using template configuration
  const defaultTitle = 'Nota de Venta'
  const formattedTitle = formatDocumentTitle(salenote.titleConfig, defaultTitle, myNumberNvv)

  const docHeader: DocHeaderType = {
    urlImage: salenote.body.organization.pictures.logotype,
    titulo: formattedTitle,
    isAnulada: esAnulada || !esAprobada,
    tituloAnulada: tituloAnulada,
    fechaIniText: `Fecha`,
    fechaIniValue: formatDate(salenote?.date),
    fechaFinText: fechaFinText,
    fechaFinValue: fechaFinValue,
  }
  const user = { name: salenote.profile_name ?? '', phone: salenote.user_phone ?? '' }

  const organizationFrame: OrganizationFrameType = {
    ...salenote.body.organization,
    user: user,
  }
  const clientFrame: ClientFrameType = {
    ...salenote.body.client,
    compraPara: salenote.compraPara,
  }

  // Use original price for vehicle display if discount exists
  const vehiclePrice = salenote.discount?.originalPrice ?? salenote.price

  const vehicleFrame: VehicleFrameType = {
    ...salenote.body.vehicle,
    price: vehiclePrice,
  }

  const costs = salenote.additionals ?? []
  const vehicles = salenote.vehicles ?? []
  const footer: string = salenote.footer ?? ''
  const price = salenote.price
  const creditAmount = salenote.credit?.amount ?? 0

  // Calculate final amount to pay (with discount if applicable)
  const finalPrice = salenote.discount?.discountedPrice ?? price
  const porReservar = costs.reduce(
    (accumulator: number, item: { cost: number }) => Number(accumulator) + item.cost,
    finalPrice
  )

  const reservado =
    (salenote.payments ?? []).reduce(
      (accumulator: number, item: { value: number }) => Number(accumulator) + item.value,
      0
    ) +
    salenote.vehicles.reduce((accumulator: number, item: { price: number }) => Number(accumulator) + item.price, 0) +
    creditAmount
  const saldo = reservado - porReservar

  // Para poder usa GridPayment

  const creditPayment: Payment[] = salenote.credit
    ? [
        {
          _id: salenote.credit?._id ?? '',
          document: makeCreditDescription(salenote.credit),
          date: formatDate(formatDateToDDMMYYYY(new Date(salenote.credit?.updatedAt ?? new Date()))),
          value: creditAmount,
        },
      ]
    : []

  const normalPayments = (salenote.payments ?? []).filter((p) => p.type !== PaymentTypeEnum.Credit)

  let docInactive: DocToHeader = {
    state: DocStateSpecial.Activo,
    date: '',
    cause: '',
    by: '',
  }

  if (salenote?.voidedAt) {
    docInactive = {
      state: DocStateSpecial.Nulo,
      date: salenote?.voidedAt ?? '',
      cause: salenote.voidedCause ?? '',
      by: salenote?.voidedBy_user ?? '',
    }
  }

  // Calculate layout configuration to determine page breaks and font size dynamically
  const notesLength = salenote?.notes
    ? Array.isArray(salenote.notes)
      ? salenote.notes.join('\n').length
      : salenote.notes.length
    : 0
  const layoutConfig = calculatePrintLayout({
    costsCount: costs.length,
    paymentsCount: normalPayments.length + creditPayment.length,
    vehiclesCount: vehicles.length,
    hasDiscount: !!salenote.discount,
    textLength: notesLength + (footer?.length ?? 0),
  })

  return (
    <>
      <style>{`
        @media print {
          html {
            font-size: ${layoutConfig.rootFontSizePx}px !important;
          }
        }
      `}</style>
      <div className="v2-print-container">
        <DocStatusHeader docName="nota de venta" state={docInactive} />
        <DocHeader header={docHeader} />

        <OrganizationFrame organization={organizationFrame} />

        <ClientFrame client={clientFrame} />

        <VehicleFrame vehicle={vehicleFrame} />
        {salenote.discount && <VehicleDiscountSummary discount={salenote.discount} />}

        {costs?.length > 0 && <GridCost title="Adicionales" paycosts={costs} />}

        <TotalDisplay title="Total a pagar" total={porReservar} />

        <GridPayment title="Pagos" payments={normalPayments ?? []} />

        {creditPayment?.length > 0 && <GridPayment title="Créditos" payments={creditPayment} />}
        {vehicles?.length > 0 && (
          <GridVeh
            title="Vehículos en Parte de Pago"
            paycosts={vehicles}
            showPurchaseLinks={showPurchaseLinks} // Only show links when explicitly requested
            isApproved={esAprobada}
          />
        )}
        <TotalDisplay title="Total pagos" total={reservado} errors={errors} />
        {saldo > 0 && <TotalDisplay title="Saldo a favor del cliente" total={saldo} name="saldo" errors={errors} />}
        {!!salenote.insuranceCo && (
          <div className="print:break-inside-avoid border border-gray-400 p-2 mt-2 grid grid-cols-2">
            <DisplayValueEx
              label="Compañia de seguros"
              value={salenote.insuranceCo}
              className="text-lg"
              valueClass="text-base font-bold"
            />
          </div>
        )}

        <div className="dynamic-print-text">
          <DocNotes notes={salenote?.notes} />

          {/* Footer content */}
          <div className="mt-8 print:mt-[1em] text-lg print:text-base">
            <hr className="w-full border-t border-gray-400 my-4 print:my-[0.5em]" />
            <Remark>{footer}</Remark>
          </div>
        </div>
      </div>
    </>
  )
}
export const TotalDisplay: React.FC<{
  title: string
  total: number
  name?: string
  errors?: { [key: string]: string }
}> = ({ title, total, name, errors }) => {
  const total_edit = formatCurrency(total)

  return (
    <div className="print:break-inside-avoid flex justify-between border border-gray-400 p-4 print:p-1 mt-4 print:mt-1 items-center">
      <div className="text-left text-2xl print:text-base font-bold">
        {/* Content for the left-aligned div */}
        {title}
      </div>
      <div className="text-right text-xl print:text-sm font-bold">
        {/* Content for the right-aligned div */}
        <DisplayValueWithError value={total_edit} errors={errors} name={name} />
      </div>
    </div>
  )
}

export const VehicleDiscountSummary: React.FC<{
  discount: {
    originalPrice: number
    discountedPrice: number
    discountAmount: number
  }
}> = ({ discount }) => {
  return (
    <div className="print:break-inside-avoid border border-gray-400 p-4 print:p-2 mb-4 print:mb-2 text-center">
      <div className="grid grid-cols-3 gap-4">
        <div>
          <div className="text-gray-600 uppercase text-sm print:text-xs">Valor Vehículo</div>
          <div className="text-xl print:text-base font-bold">{formatCurrency(discount.originalPrice)}</div>
        </div>
        <div>
          <div className="text-gray-600 uppercase text-sm print:text-xs">Descuento</div>
          <div className="text-xl print:text-base font-bold text-red-600">
            {formatCurrency(discount.discountAmount)}
          </div>
        </div>
        <div>
          <div className="text-gray-600 uppercase text-sm print:text-xs">Total Vehículo</div>
          <div className="text-xl print:text-base font-bold">{formatCurrency(discount.discountedPrice)}</div>
        </div>
      </div>
    </div>
  )
}

export default SalenoteDetail
